export const EXCEL_PHOTO_EXPORT_LIMIT = 100;

export type ExcelPhoto = { base64: string; extension: "jpeg" | "png" };

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const source = text.replace(/^\uFEFF/, "");

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (quoted) {
      if (character === '"' && source[index + 1] === '"') { cell += '"'; index += 1; }
      else if (character === '"') quoted = false;
      else cell += character;
      continue;
    }
    if (character === '"') quoted = true;
    else if (character === ",") { row.push(cell); cell = ""; }
    else if (character === "\n") {
      row.push(cell.replace(/\r$/, "")); rows.push(row); row = []; cell = "";
    } else cell += character;
  }
  if (cell || row.length) { row.push(cell.replace(/\r$/, "")); rows.push(row); }
  return rows.filter((values) => values.some((value) => value !== ""));
}

export async function buildParticipantsWorkbook(
  csvText: string,
  loadPhoto: (entryNo: string) => Promise<ExcelPhoto | null>,
  onProgress?: (completed: number, total: number) => void
): Promise<ArrayBuffer> {
  const parsed = parseCsv(csvText);
  const [csvHeaders, ...dataRows] = parsed;
  if (!csvHeaders?.length || csvHeaders[0] !== "Entry Number") throw new Error("Excel export data is invalid.");
  if (dataRows.length > EXCEL_PHOTO_EXPORT_LIMIT) {
    throw new Error(`Excel with photos is limited to ${EXCEL_PHOTO_EXPORT_LIMIT} rows. Narrow the filters and try again.`);
  }

  const excelModule = await import("exceljs");
  const workbook = new excelModule.default.Workbook();
  workbook.creator = "DKSH Myanmar GT Scan & Win";
  workbook.created = new Date();
  const sheet = workbook.addWorksheet("Participants", {
    views: [{ state: "frozen", ySplit: 1 }],
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  const headers = [...csvHeaders, "Photo"];
  const header = sheet.addRow(headers);
  header.height = 28;
  header.font = { bold: true, color: { argb: "FFFFFFFF" } };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFA70F15" } };
  header.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } };

  const widths = [22, 12, 14, 12, 24, 24, 18, 20, 20, 16, 20, 34, 22, 20, 28];
  widths.forEach((width, index) => { sheet.getColumn(index + 1).width = width; });
  const photoColumn = headers.length;
  sheet.getColumn(photoColumn).width = 114;

  for (let index = 0; index < dataRows.length; index += 1) {
    const values = dataRows[index];
    const entryNo = values[0] ?? "";
    const photo = entryNo ? await loadPhoto(entryNo) : null;
    const row = sheet.addRow([...values, photo ? "" : "Photo unavailable"]);
    row.height = 600;
    row.alignment = { vertical: "middle", wrapText: true };
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFE1E4E8" } },
        left: { style: "thin", color: { argb: "FFE1E4E8" } },
        bottom: { style: "thin", color: { argb: "FFE1E4E8" } },
        right: { style: "thin", color: { argb: "FFE1E4E8" } }
      };
    });
    if (photo) {
      const imageId = workbook.addImage(photo);
      sheet.addImage(imageId, {
        tl: { col: photoColumn - 1, row: row.number - 1 },
        ext: { width: 800, height: 800 },
        editAs: "oneCell"
      });
    }
    onProgress?.(index + 1, dataRows.length);
  }

  return await workbook.xlsx.writeBuffer() as ArrayBuffer;
}
